import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Stack } from "react-bootstrap";
import { useMemo } from "react";
import type { RequisitionStatus, RequisitionSummary } from "@ld-scorecard/shared";
import { useApi } from "../api";
import { useMe } from "../App";
import StatCard from "../components/StatCard";
import { useFlag, FLAGS } from "../flags";

const STATUS_TONE: Record<RequisitionStatus, string> = {
  open: "success",
  filled: "info",
  closed: "secondary",
};

export default function DashboardPage() {
  const api = useApi();
  const me = useMe();
  const [reqs, setReqs] = useState<RequisitionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const showKpis = useFlag(FLAGS.DASHBOARD_KPI_TILES);
  const showArchivedFilter = useFlag(FLAGS.SHOW_ARCHIVED_FILTER);

  const load = () =>
    api
      .listRequisitions()
      .then(setReqs)
      .catch((e) => setError(String(e.message ?? e)));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const active = useMemo(() => (reqs ?? []).filter((r) => !r.requisition.archived), [reqs]);
  const archivedCount = (reqs?.length ?? 0) - active.length;
  const visible = showArchived ? (reqs ?? []) : active;

  // KPIs reflect the live (non-archived) picture.
  const openCount = active.filter((r) => r.requisition.status === "open").length;
  const candidateTotal = active.reduce((a, r) => a + r.candidateCount, 0);
  const submittedTotal = active.reduce((a, r) => a + r.submittedCount, 0);

  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h1 className="h3 mb-1">Requisitions</h1>
          <p className="text-muted mb-0">Open SE roles and how each candidate panel is tracking.</p>
        </div>
        <div className="d-flex align-items-center gap-3 flex-wrap">
          {showArchivedFilter && archivedCount > 0 && (
            <Form.Check
              type="switch"
              id="show-archived"
              label={`Show archived (${archivedCount})`}
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
          )}
          {me?.role === "admin" && (
            <Button onClick={() => setShowCreate(true)}>
              <i className="bi bi-plus-lg me-1"></i>New requisition
            </Button>
          )}
        </div>
      </div>

      {showKpis && (
        <Row className="g-3 mb-4">
          <Col xs={6} lg={3}>
            <StatCard label="Requisitions" value={reqs ? active.length : "—"} icon="bi-briefcase" tone="primary" />
          </Col>
          <Col xs={6} lg={3}>
            <StatCard label="Open" value={openCount} icon="bi-folder2-open" tone="success" />
          </Col>
          <Col xs={6} lg={3}>
            <StatCard label="Candidates" value={candidateTotal} icon="bi-people" tone="info" />
          </Col>
          <Col xs={6} lg={3}>
            <StatCard label="Scorecards submitted" value={submittedTotal} icon="bi-check2-square" tone="warning" />
          </Col>
        </Row>
      )}

      {error && <p className="text-danger">{error}</p>}
      {!reqs && !error && <Spinner animation="border" />}
      {reqs?.length === 0 && (
        <p className="text-muted">
          No requisitions yet.{" "}
          {me?.role === "admin" ? "Create one to start scoring candidates." : "Ask an admin to add one."}
        </p>
      )}

      <Row xs={1} md={2} xl={3} className="g-3">
        {visible.map(({ requisition: r, candidateCount, submittedCount }) => (
          <Col key={r.reqId}>
            <Card className={`h-100 shadow-sm${r.archived ? " opacity-75" : ""}`}>
              <Card.Body className="d-flex flex-column">
                <Stack direction="horizontal" gap={2} className="mb-1">
                  <Card.Title className="mb-0">{r.title}</Card.Title>
                  {r.archived && (
                    <Badge bg="dark" className="ms-auto">
                      <i className="bi bi-archive me-1"></i>Archived
                    </Badge>
                  )}
                  <Badge
                    bg={STATUS_TONE[r.status]}
                    className={`text-capitalize${r.archived ? "" : " ms-auto"}`}
                  >
                    {r.status}
                  </Badge>
                </Stack>
                <div className="text-muted small mb-3">
                  {r.department && (
                    <div>
                      <i className="bi bi-diagram-3 me-1"></i>
                      {r.department}
                    </div>
                  )}
                  {r.hiringManager && (
                    <div>
                      <i className="bi bi-person-badge me-1"></i>
                      {r.hiringManager}
                    </div>
                  )}
                  {r.greenhouseUrl && (
                    <div>
                      <i className="bi bi-box-arrow-up-right me-1"></i>
                      <a href={r.greenhouseUrl} target="_blank" rel="noreferrer">
                        Greenhouse posting
                      </a>
                    </div>
                  )}
                </div>
                <div className="d-flex gap-4 mb-3">
                  <div>
                    <div className="fs-5 fw-bold lh-1">{candidateCount}</div>
                    <div className="text-muted" style={{ fontSize: ".72rem" }}>candidates</div>
                  </div>
                  <div>
                    <div className="fs-5 fw-bold lh-1">{submittedCount}</div>
                    <div className="text-muted" style={{ fontSize: ".72rem" }}>scorecards</div>
                  </div>
                </div>
                <Link to={`/requisitions/${r.reqId}`} className="btn btn-outline-primary btn-sm mt-auto">
                  Open requisition <i className="bi bi-arrow-right ms-1"></i>
                </Link>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {me?.role === "admin" && (
        <CreateReqModal
          show={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void load();
          }}
        />
      )}
    </>
  );
}

function CreateReqModal({
  show,
  onClose,
  onCreated,
}: {
  show: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const api = useApi();
  const [form, setForm] = useState({ title: "", department: "", hiringManager: "", greenhouseUrl: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.createRequisition({
        title: form.title.trim(),
        department: form.department.trim() || undefined,
        hiringManager: form.hiringManager.trim() || undefined,
        greenhouseUrl: form.greenhouseUrl.trim() || undefined,
      });
      setForm({ title: "", department: "", hiringManager: "", greenhouseUrl: "" });
      onCreated();
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>New requisition</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Job opening title</Form.Label>
          <Form.Control value={form.title} onChange={set("title")} autoFocus placeholder="Enterprise Solutions Engineer — West" />
        </Form.Group>
        <Row className="g-3">
          <Col md={6}>
            <Form.Label>Department</Form.Label>
            <Form.Control value={form.department} onChange={set("department")} placeholder="Sales Engineering" />
          </Col>
          <Col md={6}>
            <Form.Label>Hiring manager</Form.Label>
            <Form.Control value={form.hiringManager} onChange={set("hiringManager")} placeholder="Jane Smith" />
          </Col>
        </Row>
        <Form.Group className="mt-3">
          <Form.Label>Greenhouse job posting URL</Form.Label>
          <Form.Control type="url" value={form.greenhouseUrl} onChange={set("greenhouseUrl")} placeholder="https://…greenhouse.io/…" />
        </Form.Group>
        {error && <p className="text-danger mt-2 mb-0">{error}</p>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={!form.title.trim() || saving}>
          {saving ? "Creating…" : "Create"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
